import { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useCampaignEditorContext } from '../state/hooks/useCampaignEditorContext';
import { useTranslation } from 'react-i18next';
import { Button, Modal, SvgIcon } from '@salecycle/ui-component-library';
import { T } from '@salecycle/experimental/Typography/Typography';
import { useTheme } from 'styled-components';
import { useSaveCampaign } from '../hooks/useSaveCampaign';
import { OffsiteCampaignStatus } from 'models/offsite/Campaign/LifeCycle';

const nonBlockingPathRegex = /^\/sites\/[a-zA-Z0-9-]+\/campaigns\/bulk-retargeting\/[a-zA-Z0-9-]+\/(audience|content|scheduling)$/;

export const CampaignExitPrompt = () => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const { state } = useCampaignEditorContext();
  const history = useHistory();
  const unblockRef = useRef(null);
  const { saveCampaign, isLoading } = useSaveCampaign();

  useEffect(() => {
    unblockRef.current = history.block(location => {
      if (
        state.hasUnsavedChanges &&
        state.status !== OffsiteCampaignStatus.Triggered &&
        state.status !== OffsiteCampaignStatus.Scheduled &&
        !nonBlockingPathRegex.test(location.pathname)
      ) {
        setShowModal(true);
        setLastLocation(location);
        return false;
      }
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
      }
    };
  }, [state.hasUnsavedChanges, state.status, history]);

  const handleNavigationClick = useCallback(
    async (shouldSave: boolean) => {
      if (shouldSave) {
        await saveCampaign();
      }

      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }

      setShowModal(false);
      history.push(lastLocation.pathname);
    },
    [history, lastLocation?.pathname, saveCampaign],
  );

  if (!showModal) return null;

  return (
    <Modal
      isOpen={showModal}
      heading={t('bulkRetargetingCampaigns.editor.unsavedChanges.heading')}
      onCloseButtonClicked={() => setShowModal(false)}
    >
      <div style={{ padding: `${theme.designToken.spacing4} 0` }}>
        <T variant="body1">{t('bulkRetargetingCampaigns.editor.unsavedChanges.exitDescription')}</T>
        <T
          variant="body1"
          fontVariant="semibold"
          style={{ margin: `${theme.designToken.spacing4} 0 ${theme.designToken.spacing2}` }}
        >
          How would you like to continue?
        </T>
        <div style={{ display: 'flex', gap: theme.designToken.spacing2 }}>
          <Button onClick={() => handleNavigationClick(true)} disabled={isLoading}>
            {t('bulkRetargetingCampaigns.editor.unsavedChanges.exitPositiveAction')}
          </Button>
          <Button
            variation="delete"
            onClick={() => handleNavigationClick(false)}
            disabled={isLoading}
            leftSideIcon={<SvgIcon name="trash" />}
          >
            {t('bulkRetargetingCampaigns.editor.unsavedChanges.exitNegativeAction')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
